/**
 * Stale-chunk recovery (YARUTA-WEB-6 / YARUTA-WEB-7).
 *
 * Vite code-splits every route into a content-hashed chunk. When production
 * is promoted, those hashes change and the previous ones stop existing — but
 * anyone who already had the app open is still running the OLD index bundle,
 * holding the OLD chunk map in memory. Their next lazily-loaded route asks
 * for a file that is no longer deployed.
 *
 * Two things make that worse than a plain 404:
 *
 *  1. The site is served with a single-page fallback (`serve -s` in
 *     `railway.json`), which applies to `/assets/**` too. A missing chunk
 *     therefore returns **200 with index.html and `content-type: text/html`**
 *     rather than 404 — verified against production. The browser is handed
 *     HTML where it expected an ES module, and the import fails.
 *  2. Because it is a 200, nothing at the HTTP layer can alert on it. The
 *     only evidence is the client-side error.
 *
 * Onboarding is the most exposed surface, since it lazy-loads a chunk per
 * step — which is exactly where both production reports came from.
 *
 * The fix is a single reload: that fetches the current index.html and its
 * current chunk map. The load-bearing constraint is that it must happen at
 * most ONCE. A chunk can also be missing for reasons a reload cannot fix (a
 * broken build, an asset that never uploaded), and an unbounded retry would
 * pin the user in a reload loop and hammer the origin. So the guard is
 * belt-and-braces: an in-memory flag bounds it within a page load, and a
 * timestamp in session storage bounds it ACROSS the reload — the reload is
 * precisely what erases the in-memory one.
 */

export interface StaleChunkHost {
  addEventListener: (type: string, listener: (e: Event) => void) => void;
  reload: () => void;
  now: () => number;
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  report: (message: string, data?: Record<string, unknown>) => void;
}

const STORAGE_KEY = 'yaruta:stale-chunk-reload';

/**
 * How long a recorded reload suppresses another one.
 *
 * Long enough that a genuinely broken build cannot loop (the user sees the
 * error boundary instead, which is the honest outcome), short enough that a
 * second, later deploy is still treated as the new event it is. Deploys are
 * manual promotions, so minutes — not seconds — is the right scale.
 */
export const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

/** Vite dispatches this on `window` when a dynamic import's preload fails. */
const PRELOAD_ERROR_EVENT = 'vite:preloadError';

export function installStaleChunkRecovery(host: StaleChunkHost): void {
  // Bounds the recovery within this page load even when storage is
  // unavailable (private browsing throws on access), so losing the durable
  // guard never costs us the loop protection entirely.
  let reloadedThisLoad = false;

  const lastReloadAt = (): number | null => {
    try {
      const raw = host.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  host.addEventListener(PRELOAD_ERROR_EVENT, (event: Event) => {
    // Cancel it: Vite rethrows an uncancelled preload error, which would
    // surface a second time as an unhandled rejection on top of the
    // ErrorBoundary report we already get.
    event.preventDefault();

    const now = host.now();
    const previous = lastReloadAt();
    const withinCooldown =
      previous !== null && now - previous < RELOAD_COOLDOWN_MS;

    if (reloadedThisLoad || withinCooldown) {
      // Reloading did not fix it, so this is not a stale page — it is a
      // chunk that genuinely cannot be fetched. Let the ErrorBoundary show
      // rather than loop, and record it under a DIFFERENT message so the two
      // causes stay distinguishable in Sentry.
      host.report('Stale chunk not recovered by reload', {
        event: PRELOAD_ERROR_EVENT,
        reloadedThisLoad,
        msSinceLastReload: previous === null ? null : now - previous,
      });
      return;
    }

    reloadedThisLoad = true;
    try {
      host.setItem(STORAGE_KEY, String(now));
    } catch {
      // Storage unavailable — `reloadedThisLoad` still bounds this page load.
    }

    // Reported BEFORE reloading: the reload repairs the screen, and without
    // this nothing would record that a deploy broke a live session.
    host.report('Stale chunk detected, reloading', {
      event: PRELOAD_ERROR_EVENT,
    });
    host.reload();
  });
}
